import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Импорты контекстов и провайдеров
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './locales/i18n';

// Импорты страниц
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import MediaPage from './pages/media/MediaPage';
import { DeviceManagementPage } from './pages/devices/DeviceManagementPage';
import PlaylistsPage from './pages/playlists/PlaylistsPage';
import SchedulePage from './pages/schedule/SchedulePage';
import UsersPage from './pages/users/UsersPage';
import SettingsPage from './pages/settings/SettingsPage';
import ReportsPage from './pages/reports/ReportsPage';
import ProofOfPlayPage from './pages/reports/ProofOfPlayPage';
import ContentStatisticsPage from './pages/reports/ContentStatisticsPage';
import DevicesUptimePage from './pages/reports/DevicesUptimePage';
import ContentPlaybackPage from './pages/reports/ContentPlaybackPage';
import SystemActivityPage from './pages/reports/SystemActivityPage';
import WebBrowserPlayerPage from './pages/player/WebBrowserPlayerPage';
import MainLayout from './components/layout/MainLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  },
});

// Защищенный маршрут
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    const { t } = useTranslation();
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        {t('common.loading')}
      </div>
    );
  }
  
  return isAuthenticated ? <MainLayout>{children}</MainLayout> : <Navigate to="/login" replace />;
};

// Создание современной темы
const createModernTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';
  
  return createTheme({
    cssVariables: true,
    colorSchemes: {
      light: {
        palette: {
          mode: 'light',
          primary: {
            main: '#667eea',
            light: '#9bb5ff',
            dark: '#3651b7',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#764ba2',
            light: '#a87cd5',
            dark: '#461b71',
            contrastText: '#ffffff',
          },
          background: {
            default: '#f8fafc',
            paper: '#ffffff',
          },
          text: {
            primary: '#1a202c',
            secondary: '#4a5568',
          },
          success: {
            main: '#48bb78',
          },
          warning: {
            main: '#ed8936',
          },
          error: {
            main: '#f56565',
          },
          info: {
            main: '#4299e1',
          },
        },
      },
      dark: {
        palette: {
          mode: 'dark',
          primary: {
            main: '#90caf9',
            light: '#e3f2fd',
            dark: '#42a5f5',
            contrastText: '#000000',
          },
          secondary: {
            main: '#ce93d8',
            light: '#f3e5f5',
            dark: '#ab47bc',
            contrastText: '#000000',
          },
          background: {
            default: '#0d1117',
            paper: '#161b22',
          },
          text: {
            primary: '#f0f6fc',
            secondary: '#8b949e',
          },
          success: {
            main: '#56d364',
          },
          warning: {
            main: '#f1c40f',
          },
          error: {
            main: '#f85149',
          },
          info: {
            main: '#58a6ff',
          },
        },
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: {
        fontWeight: 800,
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 500,
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        lineHeight: 1.6,
      },
      body2: {
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#30363d #0d1117' : '#cbd5e0 #f7fafc',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? '#0d1117' : '#f7fafc',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? '#30363d' : '#cbd5e0',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: isDark ? '#484f58' : '#a0aec0',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isDark
              ? '0 1px 3px rgba(0, 0, 0, 0.3)'
              : '0 1px 3px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: isDark
                ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#58a6ff' : '#667eea',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: isDark ? '#58a6ff' : '#667eea',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });
};

// Главный компонент приложения
const AppContent: React.FC = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Создаем тему на основе системных предпочтений
  const theme = React.useMemo(
    () => createModernTheme(prefersDarkMode ? 'dark' : 'light'),
    [prefersDarkMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/player/:token" element={<WebBrowserPlayerPage />} />
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/media" element={<ProtectedRoute><MediaPage /></ProtectedRoute>} />
            <Route path="/devices" element={<ProtectedRoute><DeviceManagementPage /></ProtectedRoute>} />
            <Route path="/device-management" element={<ProtectedRoute><DeviceManagementPage /></ProtectedRoute>} />
            <Route path="/playlists" element={<ProtectedRoute><PlaylistsPage /></ProtectedRoute>} />
            <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/reports/proof-of-play" element={<ProtectedRoute><ProofOfPlayPage /></ProtectedRoute>} />
            <Route path="/reports/devices-uptime" element={<ProtectedRoute><DevicesUptimePage /></ProtectedRoute>} />
            <Route path="/reports/content-playback" element={<ProtectedRoute><ContentPlaybackPage /></ProtectedRoute>} />
            <Route path="/reports/system-activity" element={<ProtectedRoute><SystemActivityPage /></ProtectedRoute>} />
            <Route path="/reports/content-statistics" element={<ProtectedRoute><ContentStatisticsPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

// Основной App компонент
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
